import React, { useState, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import { PDFDocument } from "pdf-lib";
import * as fontkit from "@pdf-lib/fontkit";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";

const PdfMerger = () => {
    const [pdfFiles, setPdfFiles] = useState([]);
     useTheme();
    const [mergedPdfUrl, setMergedPdfUrl] = useState(null);
    const [isMerging, setIsMerging] = useState(false);
    const [error, setError] = useState("");
    const [outputFileName, setOutputFileName] = useState("merged");
    const fileInputRef = useRef(null);

    const handleFileUpload = (e) => {
        setError("");
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        // Filter only PDF files
        const pdfFiles = files.filter(
            (file) =>
                file.type === "application/pdf" || file.name.endsWith(".pdf")
        );

        if (pdfFiles.length === 0) {
            setError("Please upload valid PDF files.");
            return;
        }

        // Create file objects with preview info
        const newPdfFiles = pdfFiles.map((file) => ({
            file,
            name: file.name,
            size: (file.size / 1024).toFixed(2) + " KB",
            pages: "?", // Will be updated after analysis
            id: Date.now() + Math.random().toString(36).substr(2, 9),
        }));

        setPdfFiles((prev) => [...prev, ...newPdfFiles]);

        // Analyze PDFs to get page counts
        analyzePdfs(newPdfFiles);
    };

    const analyzePdfs = async (files) => {
        try {
            const updatedFiles = await Promise.all(
                files.map(async (pdfFile) => {
                    try {
                        const arrayBuffer = await pdfFile.file.arrayBuffer();
                        const pdfDoc = await PDFDocument.load(arrayBuffer);
                        return {
                            ...pdfFile,
                            pages: pdfDoc.getPageCount(),
                        };
                    } catch (err) {
                        console.error(`Error analyzing ${pdfFile.name}:`, err);
                        return {
                            ...pdfFile,
                            pages: "Error",
                        };
                    }
                })
            );

            setPdfFiles((prev) => {
                const otherFiles = prev.filter(
                    (f) => !files.some((newF) => newF.id === f.id)
                );
                return [...otherFiles, ...updatedFiles];
            });
        } catch (err) {
            console.error("Error analyzing PDFs:", err);
        }
    };

    const mergePdfs = async () => {
        if (pdfFiles.length < 2) {
            setError("Please upload at least 2 PDF files to merge.");
            return;
        }

        setIsMerging(true);
        setError("");

        try {
            const mergedPdf = await PDFDocument.create();
            mergedPdf.registerFontkit(fontkit);

            let totalPages = 0;

            // Process each PDF in order
            for (const pdfFile of pdfFiles) {
                try {
                    const arrayBuffer = await pdfFile.file.arrayBuffer();
                    const pdfDoc = await PDFDocument.load(arrayBuffer);

                    const pages = await mergedPdf.copyPages(
                        pdfDoc,
                        pdfDoc.getPageIndices()
                    );
                    pages.forEach((page) => mergedPdf.addPage(page));

                    totalPages += pdfDoc.getPageCount();
                } catch (err) {
                    console.error(`Error processing ${pdfFile.name}:`, err);
                    throw new Error(
                        `Failed to process "${pdfFile.name}". File may be corrupted.`
                    );
                }
            }

            // Generate merged PDF
            const mergedPdfBytes = await mergedPdf.save();
            const blob = new Blob([mergedPdfBytes], {
                type: "application/pdf",
            });
            const url = URL.createObjectURL(blob);

            setMergedPdfUrl(url);
        } catch (err) {
            setError(err.message || "Error merging PDFs. Please try again.");
            console.error(err);
        } finally {
            setIsMerging(false);
        }
    };

    const downloadMergedPdf = () => {
        if (!mergedPdfUrl) return;

        const link = document.createElement("a");
        link.href = mergedPdfUrl;
        link.download = `${outputFileName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const removeFile = (id) => {
        setPdfFiles((prev) => prev.filter((file) => file.id !== id));
        if (mergedPdfUrl) {
            URL.revokeObjectURL(mergedPdfUrl);
            setMergedPdfUrl(null);
        }
    };

    const moveFileUp = (index) => {
        if (index <= 0) return;
        const newFiles = [...pdfFiles];
        [newFiles[index], newFiles[index - 1]] = [
            newFiles[index - 1],
            newFiles[index],
        ];
        setPdfFiles(newFiles);
        if (mergedPdfUrl) {
            URL.revokeObjectURL(mergedPdfUrl);
            setMergedPdfUrl(null);
        }
    };

    const moveFileDown = (index) => {
        if (index >= pdfFiles.length - 1) return;
        const newFiles = [...pdfFiles];
        [newFiles[index], newFiles[index + 1]] = [
            newFiles[index + 1],
            newFiles[index],
        ];
        setPdfFiles(newFiles);
        if (mergedPdfUrl) {
            URL.revokeObjectURL(mergedPdfUrl);
            setMergedPdfUrl(null);
        }
    };

    const clearAll = () => {
        setPdfFiles([]);
        if (mergedPdfUrl) {
            URL.revokeObjectURL(mergedPdfUrl);
            setMergedPdfUrl(null);
        }
        setError("");
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    return (
        <>
            <HeaderSection toolId="pdfMerger" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
                <div
                    className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden
"
                >
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faFilePdf}
                                className="mr-3"
                            />
                            PDF Merger
                        </h1>
                        <p className="opacity-90">
                            Merge multiple PDF files into a single PDF document
                        </p>
                    </div>

                    <div className="space-y-6">
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 m-6">
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept=".pdf,application/pdf"
                                onChange={handleFileUpload}
                                className="hidden"
                                multiple
                            />
                            <button
                                onClick={triggerFileInput}
                                className="bg-[#74D19C] hover:bg-[#74D19C] text-white font-medium py-2 px-4 rounded-md transition duration-200"
                            >
                                Upload PDF Files
                            </button>
                            <p className="mt-2 text-sm text-gray-500">
                                Select multiple PDF files to merge
                            </p>
                        </div>

                        {pdfFiles.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label
                                        htmlFor="output-name"
                                        className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-1"
                                    >
                                        Output File Name
                                    </label>
                                    <input
                                        type="text"
                                        id="output-name"
                                        value={outputFileName}
                                        onChange={(e) =>
                                            setOutputFileName(e.target.value)
                                        }
                                        className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-end space-x-2">
                                    <button
                                        onClick={mergePdfs}
                                        disabled={
                                            isMerging || pdfFiles.length < 2
                                        }
                                        className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                                            isMerging || pdfFiles.length < 2
                                                ? "bg-gray-400"
                                                : "bg-green-500 hover:bg-green-600"
                                        } transition duration-200`}
                                    >
                                        {isMerging ? (
                                            <span className="flex items-center justify-center">
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Merging...
                                            </span>
                                        ) : (
                                            "Merge PDFs"
                                        )}
                                    </button>
                                    <button
                                        onClick={clearAll}
                                        className="py-2 px-3 bg-red-500 hover:bg-red-600 text-white rounded-md transition duration-200"
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="h-5 w-5 text-red-500"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-red-700">
                                            {error}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {pdfFiles.length > 0 && (
                            <div className="mt-6">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 mb-4">
                                    {mergedPdfUrl
                                        ? "Merged PDF Preview"
                                        : "PDF Files to Merge"}{" "}
                                    ({pdfFiles.length})
                                </h2>

                                {mergedPdfUrl ? (
                                    <div className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
                                        <div className="p-4 bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 flex justify-between items-center">
                                            <span className="font-medium text-gray-700">
                                                {outputFileName}.pdf (
                                                {pdfFiles.reduce(
                                                    (sum, file) =>
                                                        sum +
                                                        (typeof file.pages ===
                                                        "number"
                                                            ? file.pages
                                                            : 0),
                                                    0
                                                )}{" "}
                                                pages)
                                            </span>
                                            <button
                                                onClick={downloadMergedPdf}
                                                className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-1 px-3 rounded-md text-sm transition duration-200"
                                            >
                                                Download Merged PDF
                                            </button>
                                        </div>
                                        <div className="p-4 bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 flex justify-center">
                                            <iframe
                                                src={mergedPdfUrl}
                                                title="Merged PDF Preview"
                                                className="w-full h-96 border border-gray-300"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {pdfFiles.map((file, index) => (
                                            <div
                                                key={file.id}
                                                className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm"
                                            >
                                                <div className="p-3 bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 flex justify-between items-center">
                                                    <span
                                                        className="font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 truncate"
                                                        title={file.name}
                                                    >
                                                        {file.name}
                                                    </span>
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() =>
                                                                moveFileUp(
                                                                    index
                                                                )
                                                            }
                                                            disabled={
                                                                index === 0
                                                            }
                                                            className={`p-1 rounded ${
                                                                index === 0
                                                                    ? "text-gray-300 cursor-not-allowed"
                                                                    : "text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-200"
                                                            }`}
                                                            title="Move up"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-4 w-4"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M5 15l7-7 7 7"
                                                                />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                moveFileDown(
                                                                    index
                                                                )
                                                            }
                                                            disabled={
                                                                index ===
                                                                pdfFiles.length -
                                                                    1
                                                            }
                                                            className={`p-1 rounded ${
                                                                index ===
                                                                pdfFiles.length -
                                                                    1
                                                                    ? "text-gray-300 cursor-not-allowed"
                                                                    : "text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 hover:bg-gray-200"
                                                            }`}
                                                            title="Move down"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-4 w-4"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M19 9l-7 7-7-7"
                                                                />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                removeFile(
                                                                    file.id
                                                                )
                                                            }
                                                            className="p-1 text-red-500 hover:bg-red-100 rounded"
                                                            title="Remove"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-4 w-4"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="p-3 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 flex justify-between">
                                                    <span>{file.size}</span>
                                                    <span>
                                                        {file.pages}{" "}
                                                        {file.pages === 1
                                                            ? "page"
                                                            : "pages"}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <BottomSection toolId="pdfMerger" />
        </>
    );
};

export default PdfMerger;
